import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { createContext, useContext } from 'use-context-selector';

import { CAMERA_PERMISSION_HELP_LINK } from '~common';
import { ACCEPTED_IMAGE_FORMAT } from '~configs';
import { useModal, useUserAgent } from '~hooks';
import {
  FILE_LOADING_STATE,
  IPreviewFile,
  useUploadFile,
} from '~hooks/upload-file';
import { useCameraPermission } from '~providers';
import {
  convertFileSizeToMB,
  getFileExtension,
  urlToFile,
} from '~utils/file.util';
import { toastError } from '~utils/toast.util';

export enum CAMERA_STEP {
  CAPTURE = 'CAPTURE',
  PREVIEW = 'PREVIEW',
  UNSET = 'UNSET',
}

const IMAGE_CONFIG = {
  MIN_WIDTH: 1280,
  MIN_HEIGHT: 720,
  FILE_SIZE: 10, // 10MB
};

const initialContextValues = {
  cameraStep: CAMERA_STEP.CAPTURE,
  handleSetCameraStep: (_value: CAMERA_STEP) => {
    return;
  },
  isCameraOpen: false,
  openCamera: () => {
    return;
  },
  closeCamera: () => {
    return;
  },
  isCameraReady: false,
  handleSetCameraReady: (_value: boolean) => {
    return;
  },
  selectedImageSrc: null as string | null,
  handleSetSelectedImageSrc: (_value: string | null) => {
    return;
  },
  errorOnLoadingCamera: null as string | null,
  handleSetErrorOnLoadingCamera: (_value: string) => {
    return;
  },
  selectedImageFile: null as File | null,
  onChangeFile: (_e: React.ChangeEvent<HTMLInputElement>) => {
    return;
  },
  uploadedFile: null as IPreviewFile | null,
  handleSetUploadedFile: (_value: IPreviewFile | null) => {
    return;
  },
  loadingState: null as FILE_LOADING_STATE | null,
  onUploadFile: (_targetFile: File) => {
    return;
  },
  onRemoveFile: () => {
    return;
  },
  onOpenPreviewView: (_imageUrl: string) => {
    return;
  },
  caption: undefined as string | undefined,
  description: undefined as string | undefined,
  isLandscapeView: false,
};

const CameraContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
  initialImage?: IPreviewFile;
  caption?: string;
  description?: string;
  isLandscapeView?: boolean;
  fileUploadedCallback?: (file: IPreviewFile) => void; // Optional success callback
  fileRemovedCallback?: () => void; // Optional error callback
};

export const CameraProvider: React.FC<Props> = ({
  children,
  initialImage = null,
  caption,
  description,
  isLandscapeView = false,
  fileUploadedCallback,
  fileRemovedCallback,
}) => {
  const [cameraStep, setCameraStep] = useState(CAMERA_STEP.UNSET);
  const { isOpen, openModal, closeModal } = useModal();
  const [isCameraReady, setCameraReady] = useState(false);
  const [errorOnLoadingCamera, setErrorOnLoadingCamera] = useState<
    string | null
  >(null);

  // Holds the source URL of the selected image (captured or selected from gallery)
  // for display in the UI
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);

  // Stores the image file selected or captured by the user for upload purposes
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  // Contains the file data returned from the backend after a successful upload
  const [uploadedFile, setUploadedFile] = useState<IPreviewFile | null>(
    initialImage,
  );

  // Sync when initialImage arrives after mount (e.g. Formik reinitializes on rollback restore)
  useEffect(() => {
    if (initialImage?.url && !uploadedFile?.url) {
      setUploadedFile(initialImage);
    }
  }, [initialImage, initialImage?.url, uploadedFile?.url]);

  const handleSetCameraStep = useCallback((value: CAMERA_STEP) => {
    setCameraStep(value);
  }, []);

  const handleSetCameraReady = useCallback((value: boolean) => {
    setCameraReady(value);
    setErrorOnLoadingCamera(null);
  }, []);

  const onValidateImageSource = (imageSrc: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageSrc;

      img.onload = () => {
        const { width, height } = img;
        if (
          width < IMAGE_CONFIG.MIN_WIDTH ||
          height < IMAGE_CONFIG.MIN_HEIGHT
        ) {
          resolve(
            `Image dimensions must be at least ${IMAGE_CONFIG.MIN_WIDTH}x${IMAGE_CONFIG.MIN_HEIGHT}px.`,
          );
        } else {
          resolve(null);
        }
      };

      img.onerror = () => {
        resolve('Failed to load image');
      };
    });
  };

  const handleSetSelectedImageSrc = useCallback(
    async (value: string | null) => {
      if (!value) {
        setSelectedImageSrc(null);
        setSelectedImageFile(null);
        return;
      }

      const error = await onValidateImageSource(value);
      if (error) {
        toastError({ error: '', appMessage: error });
        return;
      }

      setSelectedImageSrc(value);
      const file = await urlToFile(value, 'file');
      setSelectedImageFile(file);
    },
    [setSelectedImageSrc, setSelectedImageFile],
  );

  const handleSetErrorOnLoadingCamera = useCallback(
    (value: string) => setErrorOnLoadingCamera(value),
    [],
  );

  const onValidate = (targetFile: IPreviewFile) => {
    const fileType = getFileExtension(targetFile.name ?? '');
    if (!fileType) {
      return 'File type is not valid';
    }

    const isNotSupport = !ACCEPTED_IMAGE_FORMAT.split(',').some(
      (acceptType) => acceptType.indexOf(fileType) > -1,
    );
    if (isNotSupport) {
      return `Not support file type: ${fileType}`;
    }

    const fileSize = Number(convertFileSizeToMB(targetFile?.size ?? 0));
    const isOverSize = fileSize > IMAGE_CONFIG.FILE_SIZE;
    if (isOverSize) {
      return `The size of the image is higher than ${IMAGE_CONFIG.FILE_SIZE} MB.`;
    }

    return '';
  };

  const onChangeFile: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    async (e) => {
      const targetFile = e.target.files?.[0];
      if (!targetFile) return;

      /**
       * Allow select the same file again
       */
      e.target.value = '';

      // Validate file type and size
      const fileError = onValidate(targetFile);
      if (fileError) {
        toastError({ error: '', appMessage: fileError });
        return;
      }

      const imageSrc = URL.createObjectURL(targetFile);

      // Validate image dimensions
      const imageError = await onValidateImageSource(imageSrc);
      if (imageError) {
        toastError({ error: '', appMessage: imageError });
        return;
      }

      setSelectedImageSrc(imageSrc);
      setSelectedImageFile(targetFile);
      handleSetCameraStep(CAMERA_STEP.PREVIEW);
    },
    [handleSetCameraStep],
  );

  const handleSetUploadedFile = useCallback(
    (value: IPreviewFile | null) => {
      setUploadedFile(value);
      handleSetSelectedImageSrc(null); // remove after upload
      handleSetCameraStep(CAMERA_STEP.CAPTURE);
    },
    [handleSetCameraStep, handleSetSelectedImageSrc],
  );

  const { requestCameraPermission, handleSetLatestPermissionStatus } =
    useCameraPermission();
  const { browser, deviceType } = useUserAgent();

  const openCamera = useCallback(() => {
    requestCameraPermission().then((permissionStatus) => {
      // Update latest check for camera permission
      handleSetLatestPermissionStatus(permissionStatus);

      if (permissionStatus === 'granted') {
        setCameraStep(CAMERA_STEP.CAPTURE);
        openModal();
      } else if (permissionStatus === 'denied') {
        // Get the help link based on the browser and device type
        const helpLink = CAMERA_PERMISSION_HELP_LINK[browser][deviceType] || '';

        // Display the toast message with a shortened link
        toast.error(
          <>
            Camera permissions have been denied. Follow{' '}
            <a href={helpLink} target="_blank" rel="noopener noreferrer">
              here
            </a>{' '}
            to learn how to enable camera access in your browser.
          </>,
          { toastId: 'camera-permissions-denied' },
        );
      } else if (permissionStatus === 'error') {
        toast.error(
          'Something went wrong when asking for camera permissions.',
          {
            toastId: 'ask-camera-permissions-failed',
          },
        );
      }
    });
  }, [
    requestCameraPermission,
    handleSetLatestPermissionStatus,
    openModal,
    browser,
    deviceType,
  ]);

  const closeCamera = useCallback(() => {
    setCameraStep(CAMERA_STEP.UNSET);
    closeModal();
  }, [closeModal]);

  const onOpenPreviewView = useCallback(
    (_imageUrl: string) => {
      handleSetSelectedImageSrc(_imageUrl);
      handleSetCameraStep(CAMERA_STEP.PREVIEW);
    },
    [handleSetCameraStep, handleSetSelectedImageSrc],
  );

  const { onUploadFile, onRemoveFile, loadingState } = useUploadFile({
    initialImage,
    handleUploadPhoto: (file) => {
      handleSetUploadedFile(file);
      fileUploadedCallback && fileUploadedCallback(file);
      closeCamera();
    },
    handleRemovePhoto: () => {
      handleSetUploadedFile(null);
      fileRemovedCallback && fileRemovedCallback();
    },
  });

  const providerValues = useMemo(
    () => ({
      cameraStep,
      handleSetCameraStep,

      isCameraOpen: isOpen,
      openCamera: openCamera,
      closeCamera: closeCamera,

      isCameraReady,
      handleSetCameraReady,

      selectedImageSrc,
      handleSetSelectedImageSrc,

      errorOnLoadingCamera,
      handleSetErrorOnLoadingCamera,

      selectedImageFile,
      onChangeFile,

      uploadedFile,
      handleSetUploadedFile,

      loadingState,
      onUploadFile,
      onRemoveFile,

      onOpenPreviewView,

      caption,
      description,
      isLandscapeView,
    }),
    [
      cameraStep,
      handleSetCameraStep,
      isOpen,
      openCamera,
      closeCamera,
      isCameraReady,
      handleSetCameraReady,
      selectedImageSrc,
      handleSetSelectedImageSrc,
      errorOnLoadingCamera,
      handleSetErrorOnLoadingCamera,
      selectedImageFile,
      onChangeFile,
      uploadedFile,
      handleSetUploadedFile,
      loadingState,
      onUploadFile,
      onRemoveFile,
      onOpenPreviewView,
      caption,
      description,
      isLandscapeView,
    ],
  );

  return (
    <CameraContext.Provider value={providerValues}>
      {children}
    </CameraContext.Provider>
  );
};

export const useCameraContext = () => useContext(CameraContext);
